import React, { useState } from 'react';
import { kitchenService } from '../../services';
import { toast } from 'react-toastify';

interface Form5Props {
  onNext: (data: Form5Data) => void;
  initialData?: Form5Data;
}

export interface Form5Data {
  deliveryAvailable: boolean;
  deliveryRadius: number;
  deliveryType: 'BOTH' | 'DELIVERY_ONLY' | 'PICKUP_ONLY';
  packagingCharges: number;
  minimumOrderValue: number;
}

const Form5: React.FC<Form5Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form5Data>({
    deliveryAvailable: initialData?.deliveryAvailable ?? true,
    deliveryRadius: initialData?.deliveryRadius || 0,
    deliveryType: initialData?.deliveryType || 'BOTH',
    packagingCharges: initialData?.packagingCharges || 0,
    minimumOrderValue: initialData?.minimumOrderValue || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const radiusOptions = [
    { label: 'Select Radius', value: 0 },
    { label: '1 km', value: 1000 },
    { label: '2 km', value: 2000 },
    { label: '3 km', value: 3000 },
    { label: '5 km', value: 5000 },
    { label: '10 km', value: 10000 },
    { label: '15 km', value: 15000 },
    { label: '20 km', value: 20000 },
  ];

  const deliveryTypeOptions = [
    { label: 'Select Delivery Type', value: '' },
    { label: 'Both (Delivery & Pickup)', value: 'BOTH' },
    { label: 'Delivery Only', value: 'DELIVERY_ONLY' },
    { label: 'Pickup Only', value: 'PICKUP_ONLY' },
  ];

  const handleToggle = () => {
    setFormData((prev) => ({
      ...prev,
      deliveryAvailable: !prev.deliveryAvailable,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.deliveryAvailable && !formData.deliveryRadius) {
      setError('Please select a delivery radius');
      return;
    }
    if (!formData.deliveryType) {
      setError('Please select a delivery type');
      return;
    }
    if (formData.packagingCharges < 0) {
      setError('Packaging charges must be 0 or greater');
      return;
    }
    if (formData.minimumOrderValue < 0) {
      setError('Minimum order value must be 0 or greater');
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep5(formData);
      toast.success(response.message || 'Delivery configuration saved successfully!');
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save data. Please try again.');
      toast.error(err.message || 'Failed to save delivery configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Delivery Configuration
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Delivery Available */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-Urbanist font-medium text-[#6A6A6A]">
              Delivery Available?
            </label>
            <button
              type="button"
              onClick={handleToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.deliveryAvailable ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.deliveryAvailable ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Delivery Radius */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="deliveryRadius"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Delivery Radius
            </label>
            <select
              id="deliveryRadius"
              name="deliveryRadius"
              value={formData.deliveryRadius}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {radiusOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={option.value === 0}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Delivery Type */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="deliveryType"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Delivery Type
            </label>
            <select
              id="deliveryType"
              name="deliveryType"
              value={formData.deliveryType}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
              required
            >
              {deliveryTypeOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={!option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Packaging Charges */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="packagingCharges"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Packaging Charges
            </label>
            <input
              type="number"
              id="packagingCharges"
              name="packagingCharges"
              value={formData.packagingCharges || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Charge"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Minimum Order Value */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="minimumOrderValue"
              className="text-sm font-Urbanist font-medium text-[#6A6A6A]"
            >
              Minimum Order Value
            </label>
            <input
              type="number"
              id="minimumOrderValue"
              name="minimumOrderValue"
              value={formData.minimumOrderValue || ''}
              onChange={handleChange}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="Enter Min. value for your order"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form5;